/**
 * RFC-1146 — per-variant star/archive markers for the Variants tabs.
 *
 * ## One map, applied immediately
 *
 * `markersLive` is updated optimistically the instant the user clicks a star,
 * and drives everything: the icon, the chip counts, and which tab the tile
 * sits in. Unstarring moves the tile to Archived straight away.
 *
 * This hook USED to expose a second, frozen `markersSnapshot` that alone
 * decided tab membership, so a tile never moved under the cursor. That existed
 * for the original THREE-state heart (neutral → favourite → disliked →
 * neutral): with the dislike bucket hidden by default, a tile would vanish
 * part-way through a three-click cycle, while the user still needed it to
 * finish. A binary star is a single click, so there is no partial state left
 * to protect and the deferral only made the UI look broken — the star flipped
 * and the count moved while the tile stayed put. Removed rather than kept as
 * dead weight; the misclick case is covered by the tile being one chip away
 * and one click from restored.
 *
 * ## Surviving the panel's unmount
 *
 * `UnifiedBottomPanel` renders inside `AnimatePresence` and mounts only the
 * ACTIVE tab, so closing the panel — or merely switching to Config — unmounts
 * this hook and throws its state away. Remounting from an empty map made every
 * variant read as starred for one frame, because absence means starred, so a
 * just-archived tile flashed back into Variants until the GET resolved.
 *
 * Markers are therefore held in a module-level cache keyed by project + node
 * and seeded into initial state, so a reopen paints the last known truth
 * immediately. The fetch still runs on every closed→open transition and
 * reconciles — this is stale-while-revalidate, not a cache that goes stale:
 * another collaborator's change still lands on the next open, which was the
 * point of refetching every time.
 *
 * The cache is written from `commitLive`, so optimistic writes and their
 * rollbacks keep it in step too. It is never evicted; entries are a handful of
 * short strings per visited node.
 *
 * ## Not react-query
 *
 * The write path is optimistic-with-rollback, which `useProjectRules` models
 * more directly than the read-mostly react-query hooks in this codebase. The
 * monotonic `useRef` stale-guard and closed→open fetch trigger are lifted from
 * that hook.
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import {
  deleteVariantMarker,
  getVariantMarkers,
  putVariantMarker,
} from '../api/variantMarkers'
import {
  markersByLocalPath,
  type VariantMarker,
} from '../generated/variantMarkers'
import { createNadDebug } from '../utils/debugLog'
import {
  trackVariantArchiveChanged,
  type VariantArchiveAnalytics,
} from '../analytics/nodeAnalytics'

const markerDebug = createNadDebug('dw:variant-marker:state')

export interface UseVariantMarkersResult {
  /** Optimistic marker map — drives the star, the counts and tab membership. */
  markersLive: Map<string, VariantMarker>
  /**
   * Whether `markersLive` is an ANSWER rather than merely an empty starting
   * point.
   *
   * Load-bearing, because absence means starred: an unresolved empty map is
   * indistinguishable from "no variant is archived", so a panel that renders it
   * states a confident wrong answer (every tile in Variants, "2 / 0") and then
   * corrects itself a moment later. Callers must hold the counts and the
   * partition until this is true.
   *
   * True immediately when seeded from cache. Also set on a FAILED load — a
   * permanently blocked panel is worse than one that falls back to showing
   * everything.
   */
  hasLoaded: boolean
  /**
   * Archive (`true`) or restore (`false`) one variant. Pass `analytics` to log
   * `canvas_variant_archive_changed` once the marker write succeeds.
   */
  setArchived: (
    localPath: string,
    archived: boolean,
    analytics?: VariantArchiveAnalytics,
  ) => Promise<void>
  loading: boolean
  error: string | null
}

/**
 * The marker written to archive a variant.
 *
 * Restoring DELETEs the row rather than writing `'favorite'`: under the binary
 * star model an absent marker and a legacy `'favorite'` row mean the same thing
 * (starred), so the smaller of the two representations wins and `'favorite'` is
 * never written again. See the Canvas UI 2.0 note in `variantKey.ts`.
 */
const ARCHIVED_MARKER: VariantMarker = 'disliked'

/**
 * Last known markers per (project, node), surviving the panel's unmount.
 *
 * Module-level on purpose: the point is to outlive the component. Exported
 * only so tests can start from a known state.
 */
const MARKER_CACHE = new Map<string, Map<string, VariantMarker>>()

/** @internal test seam — drop all cached markers. */
export function __clearVariantMarkerCache(): void {
  MARKER_CACHE.clear()
}

function cacheKeyFor(
  projectId: string | undefined,
  nodeId: string | undefined,
): string | null {
  if (!projectId || !nodeId) return null
  return `${projectId}::${nodeId}`
}

function readCache(key: string | null): Map<string, VariantMarker> {
  const cached = key === null ? undefined : MARKER_CACHE.get(key)
  // A copy, so a later optimistic write cannot mutate the cached map in place
  // and make a rollback un-rollbackable.
  return cached ? new Map(cached) : new Map()
}

export function useVariantMarkers(
  projectId: string | undefined,
  nodeId: string | undefined,
  isOpen: boolean,
): UseVariantMarkersResult {
  const cacheKey = cacheKeyFor(projectId, nodeId)

  // Seeded from the cache so a reopen paints the last known truth on the first
  // frame instead of an empty map (which would read as "everything starred").
  const [markersLive, setMarkersLive] = useState<Map<string, VariantMarker>>(
    () => readCache(cacheKey),
  )
  const [loading, setLoading] = useState(false)
  // Cache PRESENCE, not size: a cached empty map is a real answer ("nothing is
  // archived"), and treating it as unloaded would reintroduce the flash.
  const [hasLoaded, setHasLoaded] = useState(
    () => cacheKey !== null && MARKER_CACHE.has(cacheKey),
  )
  const [error, setError] = useState<string | null>(null)

  // Monotonic sequence: only the most recent load may commit. A late response
  // from a superseded request is dropped.
  const loadSeqRef = useRef(0)
  const wasOpenRef = useRef(false)
  // Mirrors `markersLive` for use inside callbacks without adding it as a
  // dependency (which would re-create `setArchived` on every click).
  const liveRef = useRef<Map<string, VariantMarker>>(readCache(cacheKey))
  const cacheKeyRef = useRef(cacheKey)

  const commitLive = useCallback(
    (next: Map<string, VariantMarker>) => {
      liveRef.current = next
      setMarkersLive(next)
      // Writing here rather than only after a load means optimistic updates and
      // their rollbacks stay in the cache too, so closing the panel immediately
      // after a click does not resurrect the pre-click state on reopen.
      if (cacheKey !== null) MARKER_CACHE.set(cacheKey, new Map(next))
    },
    [cacheKey],
  )

  // Re-seed if the hook is pointed at a different node without remounting.
  // `useState`/`useRef` initialisers run once, so without this the previous
  // node's markers would linger.
  useEffect(() => {
    if (cacheKeyRef.current === cacheKey) return
    cacheKeyRef.current = cacheKey
    const seeded = readCache(cacheKey)
    liveRef.current = seeded
    setMarkersLive(seeded)
    setHasLoaded(cacheKey !== null && MARKER_CACHE.has(cacheKey))
  }, [cacheKey])

  const load = useCallback(() => {
    if (!projectId || !nodeId) return
    const seq = ++loadSeqRef.current
    setLoading(true)
    setError(null)
    getVariantMarkers(projectId, { nodeId })
      .then((resp) => {
        if (seq !== loadSeqRef.current) return
        const map = markersByLocalPath(resp)
        commitLive(map)
        setLoading(false)
        setHasLoaded(true)
        // `legacy_favorite_count` is expected to be non-zero on any project
        // marked before Canvas UI 2.0 collapsed the 3-state marker to a binary
        // star. Those rows read as "starred", identically to an absent marker,
        // and are never written again — a count that stays flat while
        // `archived_count` moves is the signal that the reinterpretation is
        // behaving (see variantKey.ts).
        let legacyFavorites = 0
        let archived = 0
        map.forEach((value) => {
          if (value === 'favorite') legacyFavorites += 1
          else archived += 1
        })
        markerDebug({
          event: 'variant-marker:panel-loaded',
          trace_id: `variant-marker:${nodeId}`,
          node_id: nodeId,
          project_id: projectId,
          marker_count: map.size,
          legacy_favorite_count: legacyFavorites,
          archived_count: archived,
        })
      })
      .catch((err) => {
        if (seq !== loadSeqRef.current) return
        setError(err instanceof Error ? err.message : String(err))
        setLoading(false)
        // Unblock the panel: with no markers we fall back to showing every
        // variant under Variants, which is wrong-ish but usable. A skeleton
        // that never resolves is not.
        setHasLoaded(true)
        markerDebug({
          event: 'variant-marker:load-failed',
          trace_id: `variant-marker:${nodeId}`,
          node_id: nodeId,
          project_id: projectId,
          message: err instanceof Error ? err.message : String(err),
        })
      })
  }, [projectId, nodeId, commitLive])

  // Fetch on every closed→open transition. The cache above is a paint-first
  // seed, NOT a substitute for this: markers are shared across collaborators,
  // so another user's change must still appear on the next open (the REST
  // design's accepted seam — see api/variantMarkers.ts).
  useEffect(() => {
    if (isOpen && !wasOpenRef.current) load()
    wasOpenRef.current = isOpen
  }, [isOpen, load])

  const setArchived = useCallback(
    async (localPath: string, archived: boolean, analytics?: VariantArchiveAnalytics) => {
      if (!projectId || !nodeId) return
      const from = liveRef.current.get(localPath)
      const to = archived ? ARCHIVED_MARKER : undefined

      markerDebug({
        event: 'variant-marker:click',
        trace_id: `variant-marker:${nodeId}`,
        node_id: nodeId,
        project_id: projectId,
        local_path: localPath,
        from_marker: from ?? 'starred',
        to_marker: to ?? 'starred',
        archived,
      })

      // Optimistic: update the icon immediately, roll back on failure.
      const previous = liveRef.current
      const optimistic = new Map(previous)
      if (to === undefined) optimistic.delete(localPath)
      else optimistic.set(localPath, to)
      commitLive(optimistic)

      const startedAt = Date.now()
      try {
        if (to === undefined) {
          await deleteVariantMarker(projectId, { nodeId, localPath })
        } else {
          await putVariantMarker(projectId, { nodeId, localPath, marker: to })
        }
        markerDebug({
          event: 'variant-marker:persist-ok',
          trace_id: `variant-marker:${nodeId}`,
          node_id: nodeId,
          local_path: localPath,
          to_marker: to ?? 'starred',
          latency_ms: Date.now() - startedAt,
        })
        // RFC-1577: only after the write landed — a rolled-back click is not logged.
        if (analytics) trackVariantArchiveChanged(projectId, nodeId, archived, analytics)
      } catch (err) {
        commitLive(previous)
        setError(err instanceof Error ? err.message : String(err))
        markerDebug({
          event: 'variant-marker:persist-failed',
          trace_id: `variant-marker:${nodeId}`,
          node_id: nodeId,
          local_path: localPath,
          to_marker: to ?? 'starred',
          rolled_back: true,
          message: err instanceof Error ? err.message : String(err),
        })
      }
    },
    [projectId, nodeId, commitLive],
  )

  return {
    markersLive,
    hasLoaded,
    setArchived,
    loading,
    error,
  }
}
